import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBackgroundJobs } from '../context/BackgroundJobsContext';
import NotificationBell from './NotificationBell';

const SUBSCRIPTION_APPROVED_SEEN_KEY = 'facelink_subscription_approved_seen';

const NAV_ITEMS = [
  {
    to: '/',
    exact: true,
    label: 'Dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/events',
    label: 'My Events',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    to: '/subscription',
    label: 'Subscription',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    to: '/notifications',
    label: 'Notifications',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
  {
    to: '/help',
    label: 'Help',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 10a4 4 0 118 0c0 1.657-1.333 3-2.5 3.5M12 17h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const SidebarContent = ({ location, user, logout, onClose }) => {
  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const initials = (user?.name || user?.phone || 'U')[0].toUpperCase();

  return (
    <div className="flex flex-col h-full" style={{ background: 'linear-gradient(180deg, #1e1b4b 0%, #1a1a2e 50%, #0f0f1a 100%)' }}>
      {/* Logo */}
      <div className="flex items-center justify-between px-5 h-16 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <span className="font-bold text-white text-base tracking-tight">Pixel App</span>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group ${
                active
                  ? 'bg-white/12 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-white/6'
              }`}
              style={active ? { background: 'rgba(124,58,237,0.18)' } : {}}
            >
              <span className={`flex-shrink-0 transition-colors ${active ? 'text-violet-400' : 'text-slate-500 group-hover:text-slate-300'}`}>
                {item.icon}
              </span>
              <span className="flex-1">{item.label}</span>
              {active && <span className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="px-3 py-4 border-t border-white/10 flex-shrink-0">
        <Link
          to="/profile"
          onClick={onClose}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/6 transition-colors group mb-1"
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate leading-tight">{user?.name || '—'}</p>
            <p className="text-xs text-slate-400 truncate leading-tight">{user?.phone || '—'}</p>
          </div>
          <svg className="w-4 h-4 text-slate-500 group-hover:text-slate-300 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
        <button
          type="button"
          onClick={logout}
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 text-sm font-medium transition-all"
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </button>
      </div>
    </div>
  );
};

const UserLayout = () => {
  const { logout, user, userType, refreshUser } = useAuth();
  const { addToNotificationHistory, addCompletion } = useBackgroundJobs();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const approvedNotifiedRef = useRef(false);

  useEffect(() => {
    if (userType === 'user' && refreshUser) refreshUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userType !== 'user' || !user?.subscription?.subscription || approvedNotifiedRef.current) return;
    const sub = user.subscription.subscription;
    if (sub.status !== 'active' || !sub.approved_at) return;
    try {
      const seen = localStorage.getItem(SUBSCRIPTION_APPROVED_SEEN_KEY);
      if (seen === sub.approved_at) return;
      const planName = user.subscription?.plan?.name || 'Your plan';
      const label = `${planName} has been approved. You can now create events and use all features.`;
      const id = `sub-approved-${sub.approved_at}`;
      addToNotificationHistory({ type: 'subscription_approved', label, id });
      addCompletion({ id, type: 'subscription_approved', label });
      localStorage.setItem(SUBSCRIPTION_APPROVED_SEEN_KEY, sub.approved_at);
      approvedNotifiedRef.current = true;
    } catch (_) {}
  }, [userType, user, addToNotificationHistory, addCompletion]);

  const needsName = !user?.name || String(user.name).trim() === '';
  const needsEmail = user && typeof user.needs_email_verification === 'boolean' && user.needs_email_verification === true;
  const showProfileBanner = user && (needsName || needsEmail);
  const expiryGrace = user?.subscription?.expiry_grace;
  const inGracePeriod = expiryGrace?.in_grace_period === true;
  const expiringSoon = expiryGrace?.expires_in_days != null && expiryGrace.expires_in_days >= 1 && expiryGrace.expires_in_days <= 7;
  const showExpiryBanner = expiryGrace && (inGracePeriod || expiringSoon);
  const subscriptionAllowed = user?.subscription?.allowed === true;
  const isPending = user?.subscription?.reason?.toLowerCase().includes('pending');
  const showExpiredBanner = user && !subscriptionAllowed && !isPending;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent location={location} user={user} logout={logout} onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:w-64 lg:flex lg:flex-col">
        <SidebarContent location={location} user={user} logout={logout} onClose={null} />
      </div>

      {/* Main area */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Banners */}
        {showExpiredBanner && (
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 flex-wrap text-center text-sm font-medium bg-rose-600 text-white">
            <span>Your subscription has expired. Upload, create event, and verification are disabled until you subscribe.</span>
            <Link
              to="/subscription"
              className="inline-flex items-center px-3 py-1.5 rounded-md font-semibold whitespace-nowrap bg-white/20 hover:bg-white/30 text-white border border-white/30"
            >
              Subscribe now
            </Link>
          </div>
        )}
        {showExpiryBanner && !showExpiredBanner && (
          <div className={`px-4 py-2.5 flex items-center justify-center gap-2 flex-wrap text-center text-sm font-medium ${inGracePeriod ? 'bg-rose-500 text-white' : 'bg-amber-400 text-amber-950'}`}>
            <span>
              {inGracePeriod
                ? 'Your plan has expired. Renew within 7 days or all your data will be permanently deleted.'
                : `Your plan expires in ${expiryGrace.expires_in_days} day${expiryGrace.expires_in_days !== 1 ? 's' : ''}. Renew to avoid data loss.`}
            </span>
            <Link
              to="/subscription"
              className={`inline-flex items-center px-3 py-1 rounded-md font-semibold whitespace-nowrap ${inGracePeriod ? 'bg-white/20 hover:bg-white/30 text-white border border-white/30' : 'bg-black/15 hover:bg-black/25'}`}
            >
              Subscription
            </Link>
          </div>
        )}
        {showProfileBanner && (
          <div className="bg-amber-400 text-amber-950 px-4 py-2.5 flex items-center justify-center gap-2 flex-wrap text-center text-sm font-medium">
            <span>
              {needsName && needsEmail
                ? 'Please complete your profile: add your name and verify your email.'
                : needsName
                  ? 'Please add your name in Profile.'
                  : 'Please verify your email in Profile.'}
            </span>
            <Link
              to="/profile"
              className="inline-flex items-center px-3 py-1 rounded-md bg-amber-900/20 hover:bg-amber-900/30 text-amber-900 font-semibold whitespace-nowrap"
            >
              Go to Profile
            </Link>
          </div>
        )}

        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-sm">
          <div className="flex items-center justify-between gap-4 px-4 sm:px-6 h-16">
            <div className="flex items-center gap-3">
              {/* Mobile hamburger */}
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                aria-label="Open menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              {/* Mobile logo */}
              <div className="flex items-center gap-2 lg:hidden">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="font-bold text-slate-900 text-sm">Pixel App</span>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-4 sm:px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default UserLayout;
